<script setup>
import { computed } from 'vue';
import { formatBytes } from 'shared/helpers/FileHelper';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  uploadStates: {
    type: [Map, Object],
    default: () => new Map(),
  },
});

const emit = defineEmits(['removeAttachment']);

const { t } = useI18n();

const nonRecordedAudioAttachments = computed(() => {
  return props.attachments.filter(attachment => !attachment?.isRecordedAudio);
});

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isRecordedAudio)
);

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

const formatFileSize = file => {
  const size = file.byte_size || file.size;
  return formatBytes(size, 0);
};

const isTypeImage = file => {
  const type = file.content_type || file.type;
  return type.includes('image');
};

const fileName = file => {
  return file.filename || file.name;
};

const getUploadState = attachment => {
  const fileId = attachment.fileId || attachment.resource?.id;
  if (!fileId || !props.uploadStates) return 'idle';

  // Handle both Map and Object
  if (props.uploadStates instanceof Map) {
    return props.uploadStates.get(fileId) || 'idle';
  }
  return props.uploadStates[fileId] || 'idle';
};

const isUploading = attachment => {
  return getUploadState(attachment) === 'uploading';
};

const isUploaded = attachment => {
  return getUploadState(attachment) === 'uploaded';
};

const hasError = attachment => {
  return getUploadState(attachment) === 'error';
};
</script>

<template>
  <div class="flex flex-col overflow-auto max-h-[12.5rem]">
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id"
      class="flex items-center p-1 bg-n-slate-3 gap-1 rounded-md w-[15rem] mb-1"
    >
      <div class="max-w-[4rem] flex-shrink-0 w-6 flex items-center relative">
        <Spinner
          v-if="isUploading(attachment)"
          size="small"
          class="absolute inset-0"
        />
        <img
          v-else-if="isTypeImage(attachment.resource)"
          class="object-cover w-6 h-6 rounded-sm"
          :src="attachment.thumb"
        />
        <span v-else class="relative w-6 h-6 text-lg text-left -top-px">
          <span
            v-if="attachment.resource?.content_type?.includes('video')"
            class="i-lucide-video w-4 h-4"
          />
          <span v-else class="i-lucide-file w-4 h-4" />
        </span>
      </div>
      <div class="max-w-3/5 min-w-[50%] overflow-hidden text-ellipsis">
        <span
          class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap"
        >
          {{ fileName(attachment.resource) }}
        </span>
        <div
          v-if="isUploading(attachment)"
          class="text-xs text-n-slate-11 mt-0.5"
        >
          {{ t('TICKETS.UPLOAD.UPLOADING') }}
        </div>
        <div
          v-else-if="isUploaded(attachment)"
          class="text-xs text-n-teal-9 mt-0.5"
        >
          {{ t('TICKETS.UPLOAD.UPLOADED') }}
        </div>
        <div
          v-else-if="hasError(attachment)"
          class="text-xs text-n-ruby-9 mt-0.5"
        >
          {{ t('TICKETS.UPLOAD.FAILED') }}
        </div>
      </div>
      <div class="w-[30%] justify-center">
        <span
          v-if="!isUploading(attachment)"
          class="overflow-hidden text-xs text-ellipsis whitespace-nowrap"
        >
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center">
        <Button
          v-if="!isUploading(attachment)"
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>
  </div>
</template>
